import React from 'react';
import { MdExpandMore } from 'react-icons/md';

// Presentation primitives for the plan. These render the deck — they are not the general
// data components in DeltaUI.js, which stay as they are for the position register.
//
// MOBILE IS THE PRIMARY TARGET, not a breakpoint that gets swept up afterwards. The deck
// is a 16:9 landscape diagram: three columns side by side, eight-point body text. On a
// phone that shape cannot survive, so it is not preserved — it is re-expressed. The three
// columns become one column at a time, chosen from the relationship diagram itself, and
// that diagram doubles as the navigation. Nothing is scaled down to fit; the smallest type
// on any screen is 11px.
//
// COLOUR. Each column owns one of three tones for the whole deck: navy, teal, gold. The
// tone appears as a filled header band, a left rule, and coloured text — and a value dark
// enough to read on white is too dark to read on the dark theme, so every tone carries a
// dark-theme counterpart. Tailwind cannot express this (a runtime hex never reaches the
// compiled stylesheet), so the tone is set once as CSS custom properties on the card and
// the .tone-* rules in index.css choose per theme.

export const TONES = {
  navy: { bar: '#14375B', dk: '#7FA9D6', tint: '#14375B12', tintDk: '#7FA9D614' },
  teal: { bar: '#15706C', dk: '#5FB8B2', tint: '#15706C12', tintDk: '#5FB8B214' },
  gold: { bar: '#B8851C', dk: '#DDAF52', tint: '#B8851C14', tintDk: '#DDAF5214' },
};

const CRIMSON = '#A4192C';

// Every toned element gets these four variables; the CSS picks two of them per theme.
export const toneVars = (tone) => {
  const t = TONES[tone] || TONES.navy;
  return { '--tone': t.bar, '--tone-dk': t.dk, '--tone-tint': t.tint, '--tone-tint-dk': t.tintDk };
};

// ---------------------------------------------------------------------------
// Slide furniture
// ---------------------------------------------------------------------------

// The deck's title treatment: a serif headline and the crimson rule with its diamond.
//
// The slide's eyebrow ("PORTFOLIO ARCHITECTURE | THREE BOOKS AND AN OVERLAY") is NOT
// rendered. It earns its place on a printed slide, where it orients someone who arrived
// mid-deck; on a screen the reader navigated here deliberately and already knows. It was
// two lines of tracked capitals above every heading saying nothing the heading did not.
// The wording stays in content/deck.js, which is the verbatim transcription of the
// document — what the UI chooses to show is a separate decision.
export const SlideHead = ({ headline }) => (
  <header className="mb-5 sm:mb-7">
    <h1 className="font-serif text-[26px] leading-tight tracking-tight text-gray-900 dark:text-gray-50 sm:text-[34px]">
      {headline}<span style={{ color: CRIMSON }}>.</span>
    </h1>
    <div className="mt-2.5 flex items-center" aria-hidden="true">
      <span className="block h-[2px] w-14" style={{ background: CRIMSON }} />
      <span className="ml-1 block h-[7px] w-[7px] rotate-45" style={{ background: CRIMSON }} />
    </div>
  </header>
);

// The closing line of a slide. Italic and quiet in the deck; kept that way — it is the
// argument for the structure above it, not a caption.
export const Footnote = ({ children }) => (
  <p className="mt-6 border-t border-gray-200 pt-4 text-[12px] italic leading-relaxed text-gray-500 dark:border-gray-800 dark:text-gray-400">
    {children}
  </p>
);

// ---------------------------------------------------------------------------
// The relationship diagram
// ---------------------------------------------------------------------------

// The grid the columns and the connectors between them share. The connector tracks are a
// FIXED width rather than `auto` so the distributions arrow above can use the same
// template and land exactly over the middle column — with `auto` tracks the two rows size
// their gaps independently and the arrow drifts off centre.
// 7rem is sized to the longest inline connector label ("← PROCEEDS"); at 5.5rem it spilled
// over the neighbouring card. If a longer flow label is added, widen this with it.
export const DECK_GRID = 'lg:grid lg:grid-cols-[1fr_7rem_1fr_7rem_1fr] lg:items-start';

// For a slide whose columns have no flows between them (the operating model). Its columns
// MUST NOT use DECK_GRID: with no connectors rendered there are three children for a
// five-track template, so the middle card lands in a 7rem connector track and is crushed.
export const DECK_GRID_PLAIN = 'lg:grid lg:grid-cols-3 lg:gap-4 lg:items-start';

// Only the pool pays the Shareholder, and this is the one flow that leaves the structure —
// so it is drawn as a single unbroken line from the pool up into the Shareholder bar, with
// the label sitting ON the line rather than captioned under a floating arrowhead. A
// detached arrow above a word reads as two marks; a line interrupted by its own label reads
// as one flow, which is what it is.
//
// This only works if NOTHING sits between the bar and the columns — see SlideDeck, which
// keeps the open/collapse toolbar above the bar for exactly this reason.
export const ShareholderBar = ({ label, flowLabel, flowFrom, tone = 'teal' }) => (
  <div>
    <div className="rounded-md px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-[3px] text-white"
         style={{ background: TONES.navy.bar }}>
      {label}
    </div>
    {/* Centred on a phone (one stack); over the middle column on a desktop, using the same
        grid template as the columns so it lands exactly over the Liquidity Pool. */}
    <div className={`flex justify-center ${DECK_GRID}`}>
      <div className="hidden lg:block" />
      <div className="hidden lg:block" />
      <FlowLine label={flowLabel} origin={flowFrom} tone={tone} />
      <div className="hidden lg:block" />
      <div className="hidden lg:block" />
    </div>
  </div>
);

// The Shareholder distribution, drawn differently at the two widths ON PURPOSE.
//
// Side by side, the line lands over the middle column, so a continuous shaft from the
// Liquidity Pool into the bar is exactly true and reads as one flow.
//
// Stacked, it is ALSO true — but only because SlideDeck reorders the cards so the paying
// column sits directly beneath the bar. It did not always: with the deck's own order the
// Core book was underneath, a shaft there would have asserted a flow that does not exist,
// and this rendered a caption instead. The line is earned by the layout, not assumed — if
// that reordering is ever removed, this must go back to a caption.
export const FlowLine = ({ label, origin, tone = 'teal' }) => (
  <>
    {/* Stacked: the Liquidity Pool sits directly below the bar, so a continuous line is
        truthful here. It used to be a caption because the Core book was underneath and a
        line would have asserted a flow that does not exist. */}
    <div className="relative h-12 w-full lg:hidden" style={toneVars(tone)}>
      <span className="tone-text absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2"
            style={{ background: 'currentColor' }} aria-hidden="true" />
      <svg className="tone-text absolute left-1/2 top-0 -translate-x-1/2" width="11" height="8"
           viewBox="0 0 11 8" aria-hidden="true">
        <polygon points="5.5,0 11,8 0,8" fill="currentColor" />
      </svg>
      {label && (
        <span className="tone-text absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-[#f7f9fc] px-2 text-[8.5px] font-semibold uppercase tracking-[1.5px] dark:bg-[#0b0d12]">
          {label}
        </span>
      )}
    </div>

    {/* The label's background must match the PAGE, not a card: it is punching a hole in the
        line, so it has to paint whatever is actually behind it. Those two colours are
        AppShell's page background — change them together, or the label sits in a box. */}
    <div className="relative hidden h-14 w-full lg:block" style={toneVars(tone)}>
      <span className="tone-text absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2"
            style={{ background: 'currentColor' }} aria-hidden="true" />
      <svg className="tone-text absolute left-1/2 top-0 -translate-x-1/2" width="11" height="8"
           viewBox="0 0 11 8" aria-hidden="true">
        <polygon points="5.5,0 11,8 0,8" fill="currentColor" />
      </svg>
      {label && (
        <span className="tone-text absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-[#f7f9fc] px-2 text-[8.5px] font-semibold uppercase tracking-[1.5px] dark:bg-[#0b0d12]">
          {label}
        </span>
      )}
    </div>
  </>
);

// The height of a collapsed card's coloured header band. The side-by-side connectors match
// it exactly so their arrows sit WITHIN the boxes they join rather than hanging below them,
// which is what a fixed padding gave once two arrows had to stack. Stated once here; if the
// header's padding or type sizes change, change this with them.
const HEADER_BAND = 'lg:h-[4.75rem]';

// One arrow and its label.
//
// Vertical arrows put the label underneath (a stacked layout has the width to spare).
// Horizontal ones put it alongside, because two stacked arrow-over-label pairs are taller
// than the header band they have to fit inside. Same component either way, so the two
// orientations cannot drift in colour or weight.
export const FlowArrow = ({ label, direction, tone = 'navy', large = false }) => {
  const horizontal = direction === 'left' || direction === 'right';
  const len = large ? 30 : 22;

  // `tone-text` sets the colour; shaft and head both take it via currentColor, so the arrow
  // follows the theme without a second hard-coded value.
  const glyph = horizontal ? (
    <svg width={len} height="11" viewBox={`0 0 ${len} 11`} className="tone-text shrink-0" aria-hidden="true">
      <line x1={direction === 'right' ? 0 : len} y1="5.5"
            x2={direction === 'right' ? len - 7 : 7} y2="5.5"
            stroke="currentColor" strokeWidth="1.5" />
      <polygon points={direction === 'right'
        ? `${len},5.5 ${len - 8},0.5 ${len - 8},10.5`
        : `0,5.5 8,0.5 8,10.5`} fill="currentColor" />
    </svg>
  ) : (
    <svg width="11" height={len} viewBox={`0 0 11 ${len}`} className="tone-text shrink-0" aria-hidden="true">
      <line x1="5.5" y1={direction === 'down' ? 0 : len} x2="5.5" y2={direction === 'down' ? len - 7 : 7}
            stroke="currentColor" strokeWidth="1.5" />
      <polygon points={direction === 'down'
        ? `5.5,${len} 0.5,${len - 8} 10.5,${len - 8}`
        : '5.5,0 0.5,8 10.5,8'} fill="currentColor" />
    </svg>
  );

  const text = label && (
    <span className="tone-text whitespace-nowrap text-[8.5px] font-semibold uppercase tracking-[1.5px]">
      {label}
    </span>
  );

  return horizontal ? (
    <span className="flex items-center gap-1" style={toneVars(tone)}>{glyph}{text}</span>
  ) : (
    <span className={`flex flex-col items-center gap-1 ${large ? 'py-2' : 'py-1'}`} style={toneVars(tone)}>
      {glyph}{text}
    </span>
  );
};

// The connector between two books. Horizontal between side-by-side columns on a desktop,
// vertical between stacked cards on a phone — same flows, same labels, both directions
// drawn where both exist. The core book returns to the pool as well as drawing from it,
// and one arrow would say otherwise.
export const FlowConnector = ({ flows, fromKey, toKey }) => {
  const forward = flows.find((f) => f.from === fromKey && f.to === toKey);
  const back = flows.find((f) => f.from === toKey && f.to === fromKey);
  const pair = [forward, back].filter(Boolean);
  if (!pair.length) return null;

  return (
    <>
      {/* Stacked: the connector sits between two full-width cards. */}
      <div className="flex items-center justify-center gap-6 py-1 lg:hidden">
        {pair.map((f) => (
          <FlowArrow key={f.label} label={f.label} tone={f.tone || 'navy'}
                     direction={f.from === fromKey ? 'down' : 'up'} />
        ))}
      </div>
      {/* Side by side: exactly the header band's height, centred within it. */}
      <div className={`hidden ${HEADER_BAND} lg:flex lg:flex-col lg:items-center lg:justify-center lg:gap-2`}>
        {pair.map((f) => (
          <FlowArrow key={f.label} label={f.label} tone={f.tone || 'navy'}
                     direction={f.from === fromKey ? 'right' : 'left'} />
        ))}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// A column
// ---------------------------------------------------------------------------

// Collapsed by default: the coloured header band alone, which already carries the title and
// the one-line description. The small Core / Liquidity / Run-off chips that used to sit
// above these were removing nothing and repeating the headers verbatim, so they are gone —
// the header IS the control now.
//
// This is what makes the slide work on a phone. Three collapsed cards and their connectors
// fit on one screen, so the reader sees the whole structure before choosing where to go in,
// and opening one does not push the other two out of reach.
export const ColumnCard = ({ column, open, onToggle, children, className = '' }) => {
  const t = TONES[column.tone];
  return (
    <section
      className={`overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0d1117] ${className}`}
      style={toneVars(column.tone)}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        /* HEADER_BAND fixes this band's height from `lg` up, and the side-by-side
           connectors use the same constant — that shared height is the only reason their
           arrows land level with the boxes rather than hanging below them. */
        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-opacity hover:opacity-95 sm:px-4 ${HEADER_BAND}`}
        style={{ background: t.bar }}
      >
        <span className="min-w-0 flex-1 text-center">
          <span className="block text-[12px] font-semibold uppercase tracking-[1.5px] text-white sm:text-[13px]">
            {column.title}
          </span>
          {column.tagline && (
            <span className="mt-0.5 block text-[10px] leading-snug text-white/70">{column.tagline}</span>
          )}
        </span>
        <span className={`shrink-0 text-white/70 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
              aria-hidden="true">
          <MdExpandMore size={20} />
        </span>
      </button>

      <div className="deck-collapse" data-open={open ? 'true' : 'false'}>
        <div>
          <div className="space-y-2.5 p-2.5 sm:p-3">{children}</div>
        </div>
      </div>
    </section>
  );
};

export const Block = ({ tone, block }) => {
  const t = TONES[tone];

  // "Net return after hedging cost" is a filled panel in the deck, not a bulleted box — it
  // is the conclusion of the column rather than another component of it.
  if (block.filled) {
    return (
      <div className="rounded-md px-3 py-2.5" style={{ background: t.bar }}>
        <h3 className="text-[13px] font-semibold leading-snug text-white">{block.title}</h3>
        <p className="mt-1 text-[11.5px] leading-relaxed text-white/80">{block.body}</p>
      </div>
    );
  }

  return (
    <div className="tone-tint rounded-md border-l-[3px] px-3 py-2.5 tone-border" style={toneVars(tone)}>
      <h3 className="tone-text text-[13px] font-semibold leading-snug">{block.title}</h3>
      {block.body && (
        <p className="mt-1 text-[11.5px] leading-relaxed text-gray-600 dark:text-gray-300">{block.body}</p>
      )}
      {block.bullets && (
        <ul className="mt-1.5 space-y-1">
          {block.bullets.map((b) => (
            <li key={b} className="flex gap-1.5 text-[11.5px] leading-relaxed text-gray-600 dark:text-gray-300">
              <span className="mt-[7px] block h-[3px] w-[3px] shrink-0 rounded-full bg-gray-400 dark:bg-gray-500" />
              <span className="min-w-0">{b}</span>
            </li>
          ))}
        </ul>
      )}
      {block.callout && (
        <p className="mt-2 border-t border-black/[0.06] pt-1.5 text-[11.5px] leading-relaxed text-gray-700 dark:border-white/[0.08] dark:text-gray-200">
          <span className="tone-text font-semibold uppercase tracking-wider">{block.callout.label}:</span>{' '}
          {block.callout.text}
        </p>
      )}
    </div>
  );
};

// Every column in the deck ends by naming the documents that govern it. That link is the
// point of the whole pack, so it is a fixed part of the column rather than a footnote.
export const GovernedBy = ({ tone, items }) => (
  <div className="rounded-md bg-gray-50 px-3 py-2 dark:bg-white/[0.04]" style={toneVars(tone)}>
    <div className="text-[8.5px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500">Governed by</div>
    <div className="tone-text mt-0.5 text-[11.5px] font-semibold leading-snug">
      {items.join(' · ')}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Sequenced steps
// ---------------------------------------------------------------------------

// Build sequence, implementation waves and the eight next steps are the same shape: an
// ordered list where the order is the meaning. One component, so they cannot drift apart.
//
// On a phone the connector is a vertical rule down the left, which is what makes it read
// as a sequence rather than four unrelated cards. The deck's left-to-right chevrons only
// reappear from `sm` up, where there is width for them.
export const StepRail = ({ label, steps, numbered = false, note, trailing }) => (
  <div className="mt-6">
    {label && (
      <div className="mb-3 text-[9px] uppercase tracking-[2.5px]" style={{ color: CRIMSON }}>
        {label}
      </div>
    )}
    <ol className="relative space-y-2 sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0 lg:grid-cols-4">
      {steps.map((s, i) => {
        const t = TONES[s.tone] || TONES.navy;
        return (
          <li key={s.title}
              className="relative rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-[#0d1117]"
              style={{ ...toneVars(s.tone), borderLeftWidth: '3px', borderLeftColor: t.bar }}>
            <div className="flex items-center gap-2">
              {numbered && (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                      style={{ background: t.bar }}>
                  {s.n ?? i + 1}
                </span>
              )}
              <span className="tone-text text-[10px] font-semibold uppercase tracking-wider">{s.when}</span>
            </div>
            <h3 className="mt-1.5 text-[13px] font-semibold leading-snug text-gray-900 dark:text-gray-100">
              {s.title}
            </h3>
            <p className="mt-1 text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">{s.detail}</p>
            {s.status}
          </li>
        );
      })}
    </ol>
    {trailing}
    {note && (
      <p className="mt-3 text-[11.5px] italic leading-relaxed text-gray-500 dark:text-gray-400">{note}</p>
    )}
  </div>
);

// A full-width statement the deck sets apart from the columns — "in the meantime", and the
// approval being sought. Not a warning: an amber banner would misread the tone of a
// document that is asking for a decision, not flagging a problem.
export const StatementBar = ({ label, title, children, tone = 'navy' }) => {
  const t = TONES[tone];
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800" style={toneVars(tone)}>
      {label && (
        <div className="px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-[2.5px] text-white sm:px-4"
             style={{ background: t.bar }}>
          {label}
        </div>
      )}
      <div className="tone-tint px-3 py-3 sm:px-4">
        {title && <h2 className="tone-text font-serif text-[17px] leading-tight sm:text-[19px]">{title}</h2>}
        <div className="mt-1.5 space-y-2 text-[12px] leading-relaxed text-gray-600 dark:text-gray-300">
          {children}
        </div>
      </div>
    </section>
  );
};

export const Note = ({ children }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-800 dark:bg-white/[0.03] sm:px-4">
    <div className="text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500">In the meantime</div>
    <p className="mt-1 text-[12px] leading-relaxed text-gray-600 dark:text-gray-300">{children}</p>
  </div>
);
